import { type EditorWidget, type Theme } from 'mini-code'
import {
  WAVEFORM_BUFFER_SIZE,
  type WaveformInfo,
} from '../bytecode.ts'
import { dspscript } from '../dsp-script.ts'
import { waveformCanvases, waveformPointersMap, waveformReadOffsets } from './common.ts'

export class WaveformWidget {
  private widgetKey: string
  private waveformHeight = 60
  private waveformId: number
  private editorWidget: EditorWidget | null = null
  private contextId: string
  private shouldAnimate?: () => boolean
  private transientDataArray: Float32Array

  constructor(
    private info: WaveformInfo,
    private theme: Theme,
    shouldAnimate?: () => boolean,
  ) {
    this.widgetKey = `waveform-${info.id}-${info.line}-${info.column}`
    this.waveformId = info.id
    this.contextId = 'global'
    this.shouldAnimate = shouldAnimate
    this.transientDataArray = new Float32Array(1024)
  }

  // Provide a way for callers to associate this widget with a context (tab id, track id, etc.)
  setContextId(contextId: string): void {
    this.contextId = contextId
  }

  private getWaveformKey(): string {
    return `${this.contextId}-${this.waveformId}`
  }

  toEditorWidget(): EditorWidget {
    if (this.editorWidget) return this.editorWidget

    this.editorWidget = {
      type: 'above',
      line: this.info.line,
      column: this.info.column,
      length: this.info.length,
      height: this.waveformHeight,

      render: (ctx, x, y, width, height) => {
        width -= 3
        height -= 2

        // Ensure valid dimensions
        if (width <= 0 || height <= 0) {
          return
        }

        ctx.fillStyle = this.theme.background
        ctx.fillRect(x, y, width, height)

        const dpr = typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1
        const canvasWidth = Math.max(1, Math.floor(width * dpr))
        const canvasHeight = Math.max(1, Math.floor(height * dpr))

        const waveformKey = this.getWaveformKey()

        let canvasData = waveformCanvases.get(waveformKey)
        if (!canvasData) {
          const canvas = typeof OffscreenCanvas !== 'undefined'
            ? new OffscreenCanvas(canvasWidth, canvasHeight)
            : (() => {
              const el = document.createElement('canvas')
              el.width = canvasWidth
              el.height = canvasHeight
              return el
            })()

          const offscreenCtx = canvas.getContext('2d', {
            willReadFrequently: true,
          }) as OffscreenCanvasRenderingContext2D
          if (!offscreenCtx) {
            ctx.strokeStyle = 'rgba(100, 100, 100, 0.5)'
            ctx.strokeRect(x, y, width, height)
            return
          }

          offscreenCtx.imageSmoothingEnabled = false
          offscreenCtx.scale(dpr, dpr)

          canvasData = { canvas, ctx: offscreenCtx }
          waveformCanvases.set(waveformKey, canvasData)
        }
        else if (canvasData.canvas.width !== canvasWidth || canvasData.canvas.height !== canvasHeight) {
          if (canvasData.canvas instanceof HTMLCanvasElement) {
            canvasData.canvas.width = canvasWidth
            canvasData.canvas.height = canvasHeight
            canvasData.ctx.imageSmoothingEnabled = false
            canvasData.ctx.scale(dpr, dpr)
          }
          else if (canvasData.canvas instanceof OffscreenCanvas) {
            const newCanvas = new OffscreenCanvas(canvasWidth, canvasHeight)
            const newCtx = newCanvas.getContext('2d', { willReadFrequently: true })
            if (newCtx) {
              newCtx.imageSmoothingEnabled = false
              newCtx.scale(dpr, dpr)
              canvasData = { canvas: newCanvas, ctx: newCtx }
              waveformCanvases.set(waveformKey, canvasData)
            }
          }
          else {
            canvasData.ctx.imageSmoothingEnabled = false
            canvasData.ctx.scale(dpr, dpr)
          }
        }

        const { canvas: offscreenCanvas, ctx: offscreenCtx } = canvasData

        const isPlayingGlobal = dspscript.isPlaying()

        if (!isPlayingGlobal) {
          // When not playing, preserve the last drawn waveform (just draw the existing canvas)
          // The canvas already has the last waveform from when it was playing
          ctx.drawImage(offscreenCanvas, x, y, width, height)
          return
        }

        // Allow callers to override when animation should happen.
        if (this.shouldAnimate && !this.shouldAnimate()) {
          ctx.drawImage(offscreenCanvas, x, y, width, height)
          return
        }

        const wasmMemory = dspscript.getWasmMemory()
        const pointer = waveformPointersMap.get(this.waveformId)
        const writePositions = dspscript.getWaveformWritePositions()
        if (!wasmMemory || !pointer || !writePositions) {
          ctx.drawImage(offscreenCanvas, x, y, width, height)
          return
        }

        const bufferView = new Float32Array(wasmMemory, pointer.bufferPtr, WAVEFORM_BUFFER_SIZE)
        let readOffset = waveformReadOffsets.get(waveformKey)

        // Get write position from shared buffer (atomically read)
        const writePosition = Atomics.load(writePositions, this.waveformId)

        // Initialize read offset to write position if not set
        if (readOffset === undefined) {
          readOffset = writePosition
          waveformReadOffsets.set(waveformKey, readOffset)
        }

        // Calculate available samples (distance from read to write, wrapping around)
        const distance = (writePosition - readOffset + WAVEFORM_BUFFER_SIZE) % WAVEFORM_BUFFER_SIZE

        // Read samples needed for waveform display
        const samplesNeeded = this.transientDataArray.length
        const samplesToRead = Math.min(samplesNeeded, distance, WAVEFORM_BUFFER_SIZE)

        // Read samples from buffer
        for (let s = 0; s < samplesToRead; s++) {
          const idx = (readOffset + s) % WAVEFORM_BUFFER_SIZE
          this.transientDataArray[s] = bufferView[idx]!
        }

        // Fill remaining with zeros if needed
        for (let s = samplesToRead; s < this.transientDataArray.length; s++) {
          this.transientDataArray[s] = 0
        }

        // Update read offset (but don't consume - just read for display)
        // We want to keep reading the latest samples, so we update readOffset to stay near writePosition
        if (samplesToRead > 0) {
          readOffset = (readOffset + samplesToRead) % WAVEFORM_BUFFER_SIZE
          waveformReadOffsets.set(waveformKey, readOffset)
        }

        // Clear canvas
        offscreenCtx.fillStyle = this.theme.background
        offscreenCtx.fillRect(0, 0, width, height)

        const centerY = height / 2
        offscreenCtx.strokeStyle = '#666'
        offscreenCtx.lineWidth = 0.75
        offscreenCtx.beginPath()
        offscreenCtx.moveTo(0, centerY)
        offscreenCtx.lineTo(width, centerY)
        offscreenCtx.stroke()

        // Find trigger point (like type 2 visualizer)
        const triggerLevel = 0.01
        let triggerIndex = 0
        const searchLength = Math.floor(this.transientDataArray.length / 2)
        for (let j = 0; j < searchLength - 1; j++) {
          if (
            this.transientDataArray[j]! < triggerLevel
            && this.transientDataArray[j + 1]! >= triggerLevel
          ) {
            triggerIndex = j
            break
          }
        }

        // Draw waveform
        offscreenCtx.beginPath()
        offscreenCtx.strokeStyle = '#ddd'
        offscreenCtx.lineJoin = 'bevel'
        offscreenCtx.lineCap = 'butt'
        offscreenCtx.lineWidth = 1.35
        const amplitude = centerY
        let first = true
        for (let xPos = 0; xPos < width; xPos++) {
          const relPos = xPos / width
          const sampleIndexUnwrapped = triggerIndex + relPos * this.transientDataArray.length
          const sampleIndex = Math.round(sampleIndexUnwrapped) % this.transientDataArray.length
          const sample = this.transientDataArray[sampleIndex]!
          const yPos = centerY - sample * amplitude
          if (first) {
            offscreenCtx.moveTo(xPos, yPos)
            first = false
          }
          else {
            offscreenCtx.lineTo(xPos, yPos)
          }
        }
        offscreenCtx.stroke()

        ctx.drawImage(offscreenCanvas, x, y, width, height)
      },
    }

    return this.editorWidget
  }
}
